import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Briefcase } from "lucide-react";
import { api } from "@/lib/api";
import { searchProjects, searchPeople } from "@/components/mobile/globalSearch";

// Hledání v hlavičce úvodu na počítači (236): zakázky a lidé jako hledání v mobilním
// menu (200), stejné řazení výsledků. Ctrl K / ⌘ K skočí do pole, šipky + Enter
// otevřou výsledek, Esc zavře. Seznamy se stáhnou až při prvním písmenu.
let CACHE = { at: 0, projects: null, people: null };

export default function HomeSearch({ peopleSearch = true, className = "" }) {
  const nav = useNavigate();
  const inputRef = useRef(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [lists, setLists] = useState({ projects: CACHE.projects, people: CACHE.people });
  const hasQ = !!q.trim();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!hasQ) return undefined;
    if (CACHE.projects && Date.now() - CACHE.at < 120000) { setLists({ projects: CACHE.projects, people: CACHE.people }); return undefined; }
    let alive = true;
    const list = (r) => (Array.isArray(r.data) ? r.data : []);
    Promise.all([
      api.get("/projects/options").then(list).catch(() => []),
      peopleSearch ? api.get("/team").then(list).catch(() => []) : Promise.resolve([]),
    ]).then(([projects, people]) => {
      CACHE = { at: Date.now(), projects, people };
      if (alive) setLists({ projects, people });
    });
    return () => { alive = false; };
  }, [hasQ, peopleSearch]);

  const results = useMemo(() => {
    if (!hasQ) return [];
    const pr = lists.projects ? searchProjects(lists.projects, q, 5).rows.map((r) => ({ ...r, kind: "p" })) : [];
    const pe = peopleSearch && lists.people ? searchPeople(lists.people, q, 5).rows.map((r) => ({ ...r, kind: "u" })) : [];
    return [...pr, ...pe];
  }, [hasQ, q, lists, peopleSearch]);
  const loading = hasQ && lists.projects === null;
  useEffect(() => { setActive(0); }, [q]);

  const go = (r) => { setOpen(false); setQ(""); inputRef.current?.blur(); nav(r.to); };
  const onKeyDown = (e) => {
    if (e.key === "Escape") { setOpen(false); inputRef.current?.blur(); return; }
    if (!results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % results.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a - 1 + results.length) % results.length); }
    else if (e.key === "Enter") { e.preventDefault(); go(results[active] || results[0]); }
  };
  const mac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");
  const show = open && hasQ;

  return (
    <div className={`relative ${className}`} data-testid="home-search">
      <label className="flex items-center gap-2.5 h-11 pl-4 pr-3 rounded-full bg-card border border-border text-muted-foreground focus-within:ring-2 focus-within:ring-brand">
        <Search size={17} aria-hidden="true" />
        <input ref={inputRef} type="search" value={q} placeholder="Hledat…" aria-label="Hledat zakázky a lidi"
          role="combobox" aria-expanded={show} aria-controls="home-search-list" aria-autocomplete="list"
          onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)} onKeyDown={onKeyDown}
          className="flex-1 min-w-0 h-10 bg-transparent outline-none text-[15px] text-foreground placeholder:text-muted-foreground" />
        {!q && <kbd className="text-[11px] font-semibold text-muted-foreground whitespace-nowrap font-sans">{mac ? "⌘ K" : "Ctrl K"}</kbd>}
      </label>
      {show && (
        <div id="home-search-list" role="listbox" aria-label="Výsledky hledání"
          className="absolute right-0 top-[calc(100%+6px)] z-40 w-[min(420px,calc(100vw-32px))] max-h-[60vh] overflow-y-auto rounded-[18px] border border-border bg-card shadow-2xl p-1.5">
          {loading ? <p className="px-3 py-3 text-sm text-muted-foreground">Hledám…</p>
            : !results.length ? <p className="px-3 py-3 text-sm text-muted-foreground">Nic nenalezeno pro „{q.trim()}“</p>
            : results.map((r, i) => (
              <button key={`${r.kind}-${r.id}`} type="button" role="option" aria-selected={i === active}
                onMouseDown={(e) => e.preventDefault()} onClick={() => go(r)} onMouseEnter={() => setActive(i)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-[12px] text-left ${i === active ? "bg-secondary" : ""}`}>
                <span className="w-8 h-8 rounded-[10px] bg-secondary grid place-items-center shrink-0 text-muted-foreground text-xs font-bold">
                  {r.kind === "p" ? <Briefcase size={16} aria-hidden="true" /> : String(r.name || "?").split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase()}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold truncate">{r.name}</span>
                  <span className="block text-xs text-muted-foreground truncate">{r.kind === "p" ? "Zakázka" : "Člověk"}{r.sub ? ` · ${r.sub}` : ""}</span>
                </span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
